// UXCam Web SDK. Loaded from the bundle rather than UXCam's documented `<head>`
// snippet because recording is gated on an app key and a feature flag, neither
// of which exists until `/env` runs at the end of `<body>`.

import { FEATURES, QUERY_KEY, REACT_APP_UXCAM_APP_KEY } from 'config'
// Not via the `utils` barrel: utils imports this service, and the cycle leaves
// `isFeatureEnabled` undefined when `initialize()` runs at module evaluation.
import isFeatureEnabled from 'utils/isFeatureEnabled'
import { isAnalyticsAllowed, onAnalyticsConsentChange } from 'consent-service'
import stripeErrorFields from 'utils/stripeErrorFields'
import { normalizePathname } from 'uxcam-pages'
import {
  discardCrossDomainEvents,
  flushCrossDomainEvents,
} from './uxcamCrossDomainEvents'

const SDK_SRC = 'https://websdk-recording.uxcam.com/index.js'
const SDK_SCRIPT_ID = 'uxcam-web-sdk'

// Replaced with `_occluded_` in the recorded URL. `code` and `state` are the
// Auth0 code-exchange params that land on /post-login.
const OCCLUDED_QUERY_PARAMS = [
  'access_token',
  'code',
  'email',
  'id_token',
  'state',
  'token',
  QUERY_KEY.ACCOUNT_ID,
  QUERY_KEY.MEMBERSHIP_EXPIRATION_DATE,
  QUERY_KEY.MEMBERSHIP_NUMBER,
  QUERY_KEY.REFERRAL,
  QUERY_KEY.RETURN_TO,
  QUERY_KEY.USERNAME,
  QUERY_KEY.ZIP,
]

export const isUxCamEnabled = () =>
  !!REACT_APP_UXCAM_APP_KEY && isFeatureEnabled(FEATURES.UXCAM)

// Keeps Salesforce IDs out of the recording and groups every member's billing
// page under one URL instead of one per member.
function occludeUrl(url) {
  return normalizePathname(url)
}

// The stub queues calls on `__t`; the remote script replaces `window.uxc` and
// drains it. Because the global is replaced, always read it at call time.
function loadSdk() {
  window.uxc = {
    __t: [],
    __ak: REACT_APP_UXCAM_APP_KEY,
    __o: {
      occlusion: {
        queryParams: OCCLUDED_QUERY_PARAMS,
        url: occludeUrl,
      },
    },
    event(name, properties) {
      this.__t.push(['event', name, properties])
    },
    setUserIdentity(identity) {
      this.__t.push(['setUserIdentity', identity])
    },
    setUserProperty(key, value) {
      this.__t.push(['setUserProperty', key, value])
    },
    setUserProperties(properties) {
      this.__t.push(['setUserProperties', properties])
    },
    // Queued like the rest: consent can be withdrawn before the remote script
    // has replaced this stub.
    abort() {
      this.__t.push(['abort'])
    },
  }

  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.src = SDK_SRC
  script.async = true
  script.defer = true
  script.id = SDK_SCRIPT_ID
  script.crossOrigin = 'anonymous'
  document.head.appendChild(script)
}

function initialize() {
  // Matches the sentry/mixpanel guard: recording Cypress runs adds only noise.
  if (window.Cypress || !isUxCamEnabled()) return
  if (document.getElementById(SDK_SCRIPT_ID)) return

  // The bundle skips bootstrap while `uxc_initialised` is set, and `abort`
  // leaves it set, so a re-grant in the same visit would add a script that
  // exits without draining the new stub. Cleared here rather than on
  // withdrawal: a script already in flight when consent was withdrawn can still
  // execute afterwards and set it again.
  delete window.uxc_initialised
  loadSdk()
}

// Analytics must never break a page, so every call funnels through here.
// Returns whether the call actually reached the SDK, so callers that dedupe can
// avoid marking something as sent when it was dropped.
function callSdk(callback) {
  const sdk = window.uxc
  if (!sdk) return false

  try {
    callback(sdk)
    return true
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`WARNING: UXCam call failed: ${e}`)
    return false
  }
}

// Public calls additionally require analytics consent. `callSdk` is the ungated
// path, used only to stop recording once consent is withdrawn.
function withSdk(callback) {
  if (!isAnalyticsAllowed()) return false

  return callSdk(callback)
}

// Properties become filterable dimensions, so pass only non-personal values
// (page names, flow names, field *names*).
export function logEvent(name, properties = {}) {
  return withSdk((sdk) => sdk.event(name, properties))
}

// These sections are edited in place without touching the URL, so route
// tracking can't see them. `editor_opened` with no `editor_saved` is an
// abandoned edit.
export const EDITOR_SECTION = {
  ACCOUNT_HOLDER: 'Account Holder Information',
  CONTACT: 'Contact',
  CONTACT_DETAILS: 'Contact Details',
  PASSWORD: 'Password',
}

export function logEditorOpened(section) {
  logEvent('editor_opened', { section })
}

export function logEditorSaved(section, properties = {}) {
  logEvent('editor_saved', { section, ...properties })
}

// A toggle has no open step, so it gets its own event rather than an
// `editor_saved` that could never have a matching `editor_opened`.
export const PREFERENCE = {
  PAPERLESS: 'Go Paperless',
}

export function logPreferenceToggled(setting, enabled) {
  logEvent('preference_toggled', { setting, enabled })
}

// Payment failures never reach `handleSubmitFailWithFlashMessage`: the Stripe
// hooks resolve the raw error to a member-facing string and rethrow, and the
// intent managers flash directly. Reported from those catch blocks instead,
// where the Stripe codes still exist.
export const PAYMENT_STAGE = {
  // Could not enroll in auto-renew after the card was confirmed.
  AUTO_RENEW_ENROLLMENT: 'auto_renew_enrollment',
  // Gift charge failed. Its own stage: the gift flow never reaches
  // `handlePaymentSubmit`.
  GIFT_PURCHASE: 'gift_purchase',
  // Upgrade charge failed after the card was accepted.
  MEMBERSHIP_UPGRADE: 'membership_upgrade',
  // Could not open a PaymentIntent, so the member never reached a card form.
  PAYMENT_INTENT: 'payment_intent',
  // Card rejected at tokenization: declines, validation, missing fields.
  PAYMENT_METHOD: 'payment_method',
  // Saved card could not be replaced from Billing.
  PAYMENT_METHOD_UPDATE: 'payment_method_update',
  // Charge itself failed, after the card was accepted.
  PURCHASE: 'purchase',
  SETUP_INTENT: 'setup_intent',
  SETUP_INTENT_CONFIRM: 'setup_intent_confirm',
  // Adding Trailer Care, or the Basic -> Plus upgrade, failed.
  TRAILER_CARE_UPGRADE: 'trailer_care_upgrade',
}

export function logPaymentFailed(stage, error, properties = {}) {
  logEvent('payment_failed', {
    stage,
    ...stripeErrorFields(error),
    ...properties,
  })
}

// Adding Trailer Care and upgrading Basic -> Plus share one route, so `intent`
// is the only thing separating the two funnels.
//
// Deduped here rather than with a ref in the view: the view unmounts on the way
// to payment, so a per-mount guard wouldn't survive the round trip it exists to
// collapse.
const reportedTrailerCareViews = new Set()

export function logTrailerCareOptionsViewed(membershipId, currentType) {
  const intent = currentType == null ? 'add' : 'upgrade'
  const key = `${membershipId}:${intent}`

  if (reportedTrailerCareViews.has(key)) return

  // Marked only once delivered: a drop before consent would otherwise suppress
  // this view for the rest of the session.
  const delivered = logEvent('trailer_care_options_viewed', {
    intent,
    current_type: currentType ?? undefined,
  })

  if (delivered) reportedTrailerCareViews.add(key)
}

// The Salesforce account ID rather than an email: resolvable internally without
// putting PII in UXCam.
export function setUserIdentity(identity) {
  if (!identity) return
  withSdk((sdk) => sdk.setUserIdentity(identity))
}

export function setUserProperty(key, value) {
  withSdk((sdk) => sdk.setUserProperty(key, value))
}

export function setUserProperties(properties = {}) {
  withSdk((sdk) => sdk.setUserProperties(properties))
}

export const testExports = {
  OCCLUDED_QUERY_PARAMS,
  SDK_SRC,
  SDK_SCRIPT_ID,
  occludeUrl,
}

// Nothing loads until the visitor allows analytics cookies, and nothing is sent
// once they withdraw. `abort` ends the session and drops the SDK's activity
// listeners without re-adding them, so the recorder stays stopped; `withSdk`
// blocks custom events, and the next page load starts clean because
// `initialize` never runs without consent.
//
// Not optional-chained: `abort` is undocumented but present in the bundle (and
// in its replay queue), so a rename should surface as a warning from `callSdk`
// rather than silently leave the recorder running.
onAnalyticsConsentChange((allowed) => {
  if (allowed) {
    // Flush reads window.uxc, which initialize installs. A handoff that arrives
    // before the stub exists is left in the cookie.
    initialize()
    flushCrossDomainEvents(logEvent)
    return
  }

  callSdk((sdk) => sdk.abort())
  // So a re-grant in the same visit loads a fresh recorder. An in-flight fetch
  // still executes and drains the queued abort.
  document.getElementById(SDK_SCRIPT_ID)?.remove()
  // Withdrawal. A handoff that has not been sent yet must not go out later.
  discardCrossDomainEvents()
})
